import { useEffect, useState, useContext, useMemo } from "react";
import { Dialog, Box, IconButton, Typography, List, ListItem, ListItemText, Chip, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ForumIcon from "@mui/icons-material/Forum";
import { db } from "../../firebase";
import { collection, query, orderBy, limit, onSnapshot } from "firebase/firestore";
import { LanguageContext } from "../../context/LanguageContext";
import ConversationDetailDialogMobile from "./ConversationDetailDialogMobile";

export default function AgentConversationsDialogMobile({ open, onClose, agent, onMarkSeen }) {
  const { translate } = useContext(LanguageContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // conversationId

  useEffect(() => {
    if (!open || !agent?.id) return;
    setLoading(true);
    const ref = collection(db, "messages", `agent_${agent.id}`, "conversations");
    const q = query(ref, orderBy("updatedAt", "desc"), limit(50));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(rows);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return unsub;
  }, [open, agent?.id]);

  // Compute seen conversations from localStorage (same strategy as desktop)
  const seenSet = useMemo(() => {
    try {
      const baseKey = `tm:seenConvos:${agent?.id}`;
      const baseArr = JSON.parse(localStorage.getItem(baseKey) || "[]");
      const s = new Set(Array.isArray(baseArr) ? baseArr : []);
      const tenantIds = Array.from(new Set(items.map((i) => i.tenantId).filter(Boolean)));
      tenantIds.forEach((tid) => {
        const k = `tm:seenConvos:${tid}:${agent?.id}`;
        const arr = JSON.parse(localStorage.getItem(k) || "[]");
        if (Array.isArray(arr)) arr.forEach((id) => s.add(id));
      });
      return s;
    } catch {
      return new Set();
    }
  }, [agent?.id, items]);

  const fmt = (ts) => (ts?.seconds ? new Date(ts.seconds * 1000).toLocaleString() : "");

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      {/* Mobile top bar */}
      <Box sx={{ display: "flex", alignItems: "center", p: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Typography sx={{ flex: 1, textAlign: "center", fontWeight: 700 }}>
          {translate("Conversations") || "Conversations"}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 1.5 }}>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">{translate("Loading") || "Loading…"}</Typography>
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {(translate && translate("NoConversations")) || "No conversations"}
          </Typography>
        ) : (
          <List>
            {items.map((c) => (
              <ListItem
                key={c.id}
                button
                onClick={() => {
                  onMarkSeen && onMarkSeen(c.id);
                  setSelected(c.id);
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ForumIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {c.id}
                      </Typography>
                      {!seenSet.has(c.id) && (
                        <Chip size="small" color="success" label={(translate && translate("New")) || "New"} />
                      )}
                      {c?.userHints?.email && (
                        <Chip
                          size="small"
                          label={c.userHints.email}
                          onClick={(e) => {
                            e.stopPropagation();
                            const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(c.userHints.email)}`;
                            window.open(url, "_blank", "noopener,noreferrer");
                          }}
                          sx={{ cursor: "pointer" }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {(translate && translate("Interactions")) || "Interactions"}: {c.interactions ?? 0} · {fmt(c.updatedAt)}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      <ConversationDetailDialogMobile
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        agentId={agent?.id}
        conversationId={selected}
      />
    </Dialog>
  );
}
